<template>
  <section class="settings">
    <h1 class="page-title">Settings</h1>

    <div class="settings-grid">
      <!-- Left nav -->
      <nav class="settings-nav">
        <button :class="['snav', { on: tab === 'profile' }]" @click="tab = 'profile'">Profile</button>
        <button :class="['snav', { on: tab === 'keys' }]" @click="tab = 'keys'">API keys</button>
        <button :class="['snav', { on: tab === 'account' }]" @click="tab = 'account'">Account</button>
      </nav>

      <div class="settings-body">
        <!-- PROFILE -->
        <div v-if="tab === 'profile'" class="lk-card card-pad">
          <div class="card-title">Profile</div>
          <div class="avatar-row">
            <div class="avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div class="avatar-note">{{ user.email || 'Signed in' }}</div>
          </div>
          <div class="fields">
            <div>
              <label class="lk-label">EMAIL</label>
              <input class="lk-input mono" :value="user.email || '—'" readonly />
              <div class="hint">This is the email you sign in with.</div>
            </div>
            <div>
              <button class="lk-btn" @click="changePassword">Change password</button>
            </div>
          </div>
        </div>

        <!-- API KEYS -->
        <div v-if="tab === 'keys'" class="lk-card card-pad">
          <div class="card-title">API keys</div>
          <p class="muted-p">Authenticate requests with the <span class="mono chip">X-API-Key</span> header.</p>

          <div class="key-create">
            <input v-model="newKeyName" class="lk-input" placeholder="Key name (e.g. Production)" @keydown.enter="createKey" />
            <button class="lk-btn lk-btn-primary" @click="createKey">+ Create key</button>
          </div>

          <!-- reveal -->
          <div v-if="revealedKey" class="reveal">
            <div class="reveal-warn">⚠ Copy your key now — you won't see it again</div>
            <div class="reveal-row">
              <span class="mono reveal-val">{{ revealedKey.api_key }}</span>
              <CopyButton :text="revealedKey.api_key" small />
            </div>
            <button class="lk-btn-ghost dismiss" @click="revealedKey = null">I've saved it — dismiss</button>
          </div>

          <div v-if="keys.length" class="key-list lk-card">
            <div v-for="k in keys" :key="k.id" class="key-row">
              <div class="key-meta">
                <div class="key-name">{{ k.name }}</div>
                <div class="key-sub mono">created {{ fmtDate(k.created_at) }} · used {{ k.last_used_at ? fmtDate(k.last_used_at) : 'never' }}<span v-if="!k.is_active"> · revoked</span></div>
              </div>
              <button class="lk-btn lk-btn-sm revoke" @click="revokeKey(k.id)">Revoke</button>
            </div>
          </div>
          <div v-else-if="!keysLoading" class="keys-empty">No API keys yet.</div>
        </div>

        <!-- ACCOUNT -->
        <div v-if="tab === 'account'" class="account-stack">
          <div class="lk-card card-pad">
            <div class="card-title">Session</div>
            <p class="muted-p">You're securely signed in.</p>
            <button class="lk-btn" @click="logout">Sign out</button>
          </div>

          <div class="lk-card card-pad">
            <button class="acc-toggle" @click="toggleJwtTokens">
              <span>Developer · JWT tokens</span>
              <span>{{ showJwtTokens ? '▲' : '▼' }}</span>
            </button>
            <div v-if="showJwtTokens" class="tokens">
              <div v-if="idToken" class="token-block">
                <div class="token-head"><span>ID token</span><CopyButton :text="idToken" small /></div>
                <pre class="token-pre mono">{{ idToken }}</pre>
              </div>
              <div v-if="accessToken" class="token-block">
                <div class="token-head"><span>Access token</span><CopyButton :text="accessToken" small /></div>
                <pre class="token-pre mono">{{ accessToken }}</pre>
              </div>
            </div>
          </div>

          <div class="lk-card card-pad danger">
            <div class="card-title danger-title">Danger zone</div>
            <p class="muted-p">Permanently delete your account and all links &amp; files. This isn't self-service yet.</p>
            <button class="lk-btn lk-btn-solid-danger" @click="requestDelete">Delete account</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import apiClient from '@/axios';
import CopyButton from '@/components/ui/CopyButton.vue';
import { fmtDate } from '@/utils/format';

export default {
  name: 'ProfileView',
  components: { CopyButton },
  data() {
    return {
      tab: 'profile',
      showJwtTokens: false,
      keys: [],
      keysLoading: false,
      newKeyName: '',
      revealedKey: null,
    };
  },
  computed: {
    ...mapState('auth', ['user', 'idToken', 'accessToken']),
  },
  created() {
    this.loadKeys();
  },
  methods: {
    ...mapActions('auth', ['fetchAuthKeys']),
    fmtDate,
    async loadKeys() {
      this.keysLoading = true;
      try {
        const res = await apiClient.get('auth/api-keys');
        this.keys = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.keys = [];
      } finally {
        this.keysLoading = false;
      }
    },
    async createKey() {
      const name = (this.newKeyName || '').trim();
      if (!name) { toast.error('Give your key a name'); return; }
      try {
        const res = await apiClient.post('auth/api-keys', { name });
        this.revealedKey = res.data;
        this.newKeyName = '';
        toast.success('API key created');
        this.loadKeys();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to create key');
      }
    },
    async revokeKey(id) {
      try {
        await apiClient.delete(`auth/api-keys/${id}`);
        toast.success('API key revoked');
        this.loadKeys();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to revoke key');
      }
    },
    toggleJwtTokens() {
      if (!this.showJwtTokens) this.fetchAuthKeys();
      this.showJwtTokens = !this.showJwtTokens;
    },
    async logout() {
      // Clear the session (and cached data) first, then navigate, so we never
      // land on a logged-out route while stale auth state lingers.
      await this.$store.dispatch('auth/logout');
      this.$router.push('/');
    },
    changePassword() {
      this.$router.push({ name: 'ChangePassword' });
    },
    requestDelete() {
      toast.info('Account deletion isn\'t self-service yet — contact support@linker.to');
    },
  },
};
</script>

<style scoped>
.settings {
  max-width: 860px;
  margin: 0 auto;
  padding: clamp(24px, 4vw, 44px) clamp(16px, 4vw, 40px) 80px;
}
.page-title { font-size: 34px; font-weight: 400; letter-spacing: -.01em; margin: 0 0 24px; }

.settings-grid { display: grid; grid-template-columns: 200px 1fr; gap: 28px; align-items: start; }
.settings-nav { display: flex; flex-direction: column; gap: 3px; position: sticky; top: 76px; }
.snav {
  text-align: left;
  border: none;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  padding: 9px 13px;
  border-radius: var(--radius);
  background: transparent;
  color: var(--muted);
}
.snav.on { background: var(--accent-soft); color: var(--accent); }

.card-pad { padding: 22px; }
.card-title { font-size: 15px; font-weight: 700; margin-bottom: 18px; }

.avatar-row { display: flex; align-items: center; gap: 16px; margin-bottom: 22px; }
.avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar svg { width: 26px; height: 26px; }
.avatar-note { font-size: 13px; color: var(--muted); font-family: var(--font-mono); }

.fields { display: flex; flex-direction: column; gap: 16px; max-width: 400px; }
.hint { font-size: 11.5px; color: var(--faint); margin-top: 6px; }

.muted-p { font-size: 13px; color: var(--muted); margin: 0 0 18px; }
.chip { background: var(--surface); padding: 2px 6px; border-radius: var(--radius); }

.key-create { display: flex; gap: 8px; margin-bottom: 20px; }
.key-create .lk-input { flex: 1; }

.reveal {
  background: var(--warn-soft);
  border: 1px solid var(--warn);
  border-radius: var(--radius);
  padding: 16px;
  margin-bottom: 18px;
  animation: lk-pop .3s ease both;
}
.reveal-warn { font-size: 13px; font-weight: 700; color: var(--warn); margin-bottom: 10px; }
.reveal-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 10px 13px;
}
.reveal-val { flex: 1; font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dismiss { margin-top: 12px; border: none; background: transparent; color: var(--muted); font-size: 12.5px; font-weight: 600; cursor: pointer; }

.key-list { overflow: hidden; }
.key-row { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-bottom: 1px solid var(--border); }
.key-row:last-child { border-bottom: none; }
.key-meta { flex: 1; min-width: 0; }
.key-name { font-size: 13.5px; font-weight: 600; }
.key-sub { font-size: 12px; color: var(--muted); }
.revoke { color: var(--danger); }
.keys-empty { font-size: 13px; color: var(--muted); }

.account-stack { display: flex; flex-direction: column; gap: 16px; }
.acc-toggle {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}
.tokens { margin-top: 16px; display: flex; flex-direction: column; gap: 16px; }
.token-head { display: flex; align-items: center; justify-content: space-between; font-size: 12.5px; font-weight: 600; color: var(--muted); margin-bottom: 8px; }
.token-pre {
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 12px;
  border-radius: var(--radius);
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
}
.danger { background: var(--danger-soft); border-color: var(--danger); }
.danger-title { color: var(--danger); }

.mono { font-family: var(--font-mono); }

@media (max-width: 760px) {
  .settings-grid { grid-template-columns: 1fr; }
  .settings-nav { position: static; flex-direction: row; flex-wrap: wrap; }
}
</style>
